import type { OrgLeaderDto } from '../api/hr-org-leader';

/**
 * 결재 사다리의 **빈 칸 세기** (2026-08-28).
 *
 * ── 왜 필요한가 ──────────────────────────────────────────────────────────
 * 사다리(팀장 → 부서장 → 대표)는 **빈 자리를 건너뛰고 위로** 올라간다. 그 자체는 설계대로지만,
 * 비어 있다는 사실을 **아무도 모른 채** 결재가 한 칸씩 짧아진다는 것이 문제다. 지금 화면은
 * 줄마다 「없음 — 이 단계를 건너뜁니다」 라고 적지만, 팀이 서른 개면 아무도 다 읽지 않는다.
 *
 * ★★ **세는 것이 요점이다.** 「팀장이 빈 팀 5개」보다 **「5개 팀 37명」** 이 사람을 움직인다.
 * ★★ **대표가 비면 이야기가 다르다** — 사다리의 끝이라 그 위가 없다. 부서장까지 비면
 *    그 회사의 결재는 팀장 한 칸으로 끝난다. 그래서 대표는 «심각» 으로 따로 센다.
 * ★ **팀이 없는 사람**(미배정)은 팀장 칸 자체가 없다 — 여기서는 세지 않는다(조직도의 일이다).
 */

export interface LadderGaps {
  /** 대표가 지정돼 있지 않다 — 사다리의 끝이 비었다. */
  noRepresentative: boolean;
  /** 부서장이 빈 부서. */
  divisionsWithoutLeader: string[];
  /** 팀장이 빈 팀과 그 인원. */
  teamsWithoutLeader: Array<{ name: string; memberCount: number }>;
  /** 팀장이 빈 팀들의 인원 합 — 「몇 명이 그 칸을 건너뛰나」. */
  affectedByTeamGap: number;
  /** 하나라도 비었나. */
  any: boolean;
  /**
   * ★★ **가장 나쁜 조합** — 대표도 부서장도 없는데 팀장만 있는 상태.
   *    이때 그 부서 사람들의 결재는 **팀장 한 칸**으로 끝난다(사실상 결재가 없는 것과 같다).
   */
  divisionsCollapsed: string[];
}

export function countLadderGaps(data: OrgLeaderDto | null | undefined): LadderGaps {
  const empty: LadderGaps = {
    noRepresentative: false, divisionsWithoutLeader: [], teamsWithoutLeader: [],
    affectedByTeamGap: 0, any: false, divisionsCollapsed: [],
  };
  if (!data) return empty;

  const noRepresentative = data.representative == null;
  const divisionsWithoutLeader = data.divisions
    .filter((d) => !d.leaderUserId)
    .map((d) => d.name);

  const teamsWithoutLeader = data.teams
    .filter((t) => !t.leaderUserId)
    // ★ 옛 서버는 인원을 안 준다 → 0 으로 센다. 「팀 N개」는 그래도 맞다.
    .map((t) => ({ name: t.name, memberCount: t.memberCount ?? 0 }));

  const affectedByTeamGap = teamsWithoutLeader.reduce((sum, t) => sum + t.memberCount, 0);

  /**
   * 무너진 부서 — 대표가 없고 그 부서의 부서장도 없다.
   * ★ 대표가 있으면 부서장이 비어도 대표까지는 올라간다 — 짧아질 뿐 «없어지지» 않는다.
   */
  const divisionsCollapsed = noRepresentative ? divisionsWithoutLeader : [];

  return {
    noRepresentative,
    divisionsWithoutLeader,
    teamsWithoutLeader,
    affectedByTeamGap,
    divisionsCollapsed,
    any: noRepresentative || divisionsWithoutLeader.length > 0 || teamsWithoutLeader.length > 0,
  };
}

/** 목록을 짧게 — 셋까지 이름을 적고 나머지는 숫자로. 이름을 다 적으면 아무도 안 읽는다. */
export function briefNames(names: readonly string[], keep = 3): string {
  if (names.length === 0) return '';
  if (names.length <= keep) return names.join(' · ');
  return `${names.slice(0, keep).join(' · ')} 외 ${names.length - keep}개`;
}
